import React, { useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import axios from 'axios';
import { selectEnrichedModules, selectLoading, selectSelectedClient, selectSelectedBranch, selectSelectedUser, fetchUserModules, updateModuleStatus, bulkUpdateStatus } from '../store/slices/usermodulesSlice';
import { getSalesReactBaseUrl } from '../utilities/api';
import './CategoryMaster.css';

const API_URL = `${getSalesReactBaseUrl()}/api/server_masters`;

const STATUS_OPTIONS = [
    { label: 'Active', value: 'Active' },
    { label: 'InActive', value: 'InActive' }
];

const UserModulesContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const enrichedModules = useSelector(selectEnrichedModules);
    const loading = useSelector(selectLoading);
    const selectedClient = useSelector(selectSelectedClient);
    const selectedBranch = useSelector(selectSelectedBranch);
    const selectedUser = useSelector(selectSelectedUser);

    const [statusFilter, setStatusFilter] = useState('All');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        module_group_name: { value: null, matchMode: FilterMatchMode.EQUALS }
    });
    const [selection, setSelection] = useState([]);
    const [bulkStatus, setBulkStatus] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState(null);

    const groupFilteredData = useMemo(() => {
        let data = enrichedModules;
        if (selectedClient) data = data.filter((m) => String(m.client_id) === String(selectedClient));
        if (selectedBranch) data = data.filter((m) => !m.branch_id || String(m.branch_id) === String(selectedBranch));
        if (selectedUser) data = data.filter((m) => String(m.user_id) === String(selectedUser));
        return data;
    }, [enrichedModules, selectedClient, selectedBranch, selectedUser]);

    const activeCount = useMemo(() => groupFilteredData.filter((m) => m.status === 'Active').length, [groupFilteredData]);
    const inactiveCount = useMemo(() => groupFilteredData.filter((m) => m.status === 'Inactive' || m.status === 'InActive').length, [groupFilteredData]);
    const totalCount = groupFilteredData.length;

    const filteredData = useMemo(() => {
        if (statusFilter === 'Active') return groupFilteredData.filter((m) => m.status === 'Active');
        if (statusFilter === 'InActive') return groupFilteredData.filter((m) => m.status === 'Inactive' || m.status === 'InActive');
        return groupFilteredData;
    }, [groupFilteredData, statusFilter]);

    const moduleGroupOptions = useMemo(() => {
        const groups = [...new Set(groupFilteredData.map((m) => m.module_group_name).filter(Boolean))].sort();
        return groups.map((g) => ({ label: g, value: g }));
    }, [groupFilteredData]);

    const handleGroupFilterChange = (value) => {
        setSelectedGroupFilter(value);
        setFilters((prev) => ({ ...prev, module_group_name: { value, matchMode: FilterMatchMode.EQUALS } }));
    };

    const reload = () => dispatch(fetchUserModules());

    // ── Bulk status change ────────────────────────────────────────────────────
    const handleBulkApply = () => {
        if (!bulkStatus) {
            toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please select a status to apply', life: 3000 });
            return;
        }
        confirmDialog({
            message: `Change status of ${selection.length} selected module(s) to "${bulkStatus}"?`,
            header: 'Confirm Bulk Update',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Apply',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: async () => {
                setBulkLoading(true);
                try {
                    const module_user_ids = selection.map((r) => r.module_user_id).filter(Boolean);
                    await axios.post(`${API_URL}/bulkUpdateModuleUserStatus`, { module_user_ids, status: bulkStatus }, { headers: { 'Content-Type': 'application/json' } });
                    dispatch(bulkUpdateStatus({ module_user_ids, status: bulkStatus }));
                    toast.current.show({ severity: 'success', summary: 'Updated', detail: `${module_user_ids.length} module(s) set to ${bulkStatus}`, life: 3000 });
                    setSelection([]);
                    setBulkStatus(null);
                } catch (err) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Bulk update failed', life: 4000 });
                } finally {
                    setBulkLoading(false);
                }
            }
        });
    };

    // ── Assign inactive modules ───────────────────────────────────────────────
    const handleAssignInactive = () => {
        if (!selectedClient || !selectedUser) {
            toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please select a client and user first', life: 3000 });
            return;
        }
        const inactiveModules = groupFilteredData.filter((m) => m.status === 'Inactive' || m.status === 'InActive');
        const moduleMenuIds = inactiveModules.map((m) => m.module_menu_id).filter(Boolean);
        if (moduleMenuIds.length === 0) {
            toast.current.show({ severity: 'info', summary: 'Info', detail: 'No inactive modules to assign', life: 3000 });
            return;
        }
        confirmDialog({
            message: `Assign ${moduleMenuIds.length} inactive module(s) to this user?`,
            header: 'Confirm Assignment',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Proceed',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: async () => {
                try {
                    const payload = { client_id: selectedClient, user_id: selectedUser, module_menu_ids: moduleMenuIds };
                    if (selectedBranch) payload.branch_id = selectedBranch;
                    await axios.post(`${API_URL}/assignInactiveModulesToUser`, payload, { headers: { 'Content-Type': 'application/json' } });
                    toast.current.show({ severity: 'success', summary: 'Success', detail: 'Inactive modules assigned successfully', life: 3000 });
                    reload();
                } catch (err) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Failed to assign modules', life: 4000 });
                }
            }
        });
    };

    // ── Cell templates ────────────────────────────────────────────────────────
    const statusBodyTemplate = (rowData) => <Tag value={rowData.status} severity={rowData.status === 'Active' ? 'success' : 'danger'} rounded style={{ minWidth: '70px', textAlign: 'center' }} />;

    const iconBodyTemplate = (rowData) => (
        <span className="flex align-items-center gap-2">
            <i className={rowData.icon} style={{ fontSize: '1.1rem', color: 'var(--primary-color)' }} />
            <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{rowData.icon}</span>
        </span>
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* ── Top bar ── */}
            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">User Modules List</h3>
                    <div className="category-status-tabs">
                        <button className={`status-tab-btn${statusFilter === 'All' ? ' selected active-tab' : ''}`} onClick={() => setStatusFilter('All')}>
                            <i className="pi pi-list" /> All <span className="tab-count">{totalCount}</span>
                        </button>
                        <button className={`status-tab-btn active-tab${statusFilter === 'Active' ? ' selected' : ''}`} onClick={() => setStatusFilter('Active')}>
                            <i className="pi pi-check-circle" /> Active <span className="tab-count">{activeCount}</span>
                        </button>
                        <button className={`status-tab-btn inactive-tab${statusFilter === 'InActive' ? ' selected' : ''}`} onClick={() => setStatusFilter('InActive')}>
                            <i className="pi pi-times-circle" /> Inactive <span className="tab-count">{inactiveCount}</span>
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 align-items-center">
                    <Dropdown value={selectedGroupFilter} options={moduleGroupOptions} onChange={(e) => handleGroupFilterChange(e.value)} placeholder="Module Group" showClear style={{ minWidth: '180px' }} />
                    <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={(e) => {
                                setGlobalFilterValue(e.target.value);
                                setFilters((prev) => ({ ...prev, global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } }));
                            }}
                            placeholder="Search user modules..."
                            className="w-full"
                        />
                    </span>
                </div>
            </div>

            {/* ── Bulk action bar (visible only when rows are selected) ── */}
            {selection.length > 0 && (
                <div className="flex align-items-center gap-3 mb-3 p-3 border-round" style={{ background: 'linear-gradient(135deg,#f0e7ff 0%,#e9d8ff 100%)', border: '1.5px solid var(--primary-color)' }}>
                    <i className="pi pi-check-square" style={{ color: 'var(--primary-color)', fontSize: '1.2rem' }} />
                    <span className="font-semibold" style={{ color: 'var(--primary-color)' }}>
                        {selection.length} row{selection.length > 1 ? 's' : ''} selected
                    </span>
                    <Dropdown value={bulkStatus} options={STATUS_OPTIONS} onChange={(e) => setBulkStatus(e.value)} placeholder="Change status to..." style={{ width: '180px' }} />
                    <Button label="Apply" icon="pi pi-check" className="p-button-success p-button-sm" onClick={handleBulkApply} loading={bulkLoading} disabled={!bulkStatus} />
                    <Button
                        label="Clear"
                        icon="pi pi-times"
                        className="p-button-text p-button-sm"
                        onClick={() => {
                            setSelection([]);
                            setBulkStatus(null);
                        }}
                    />
                </div>
            )}

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="sub-masters-table-wrap">
                    <DataTable
                        value={filteredData}
                        filters={filters}
                        globalFilterFields={['client_name', 'branch_name', 'username', 'module_name', 'module_group_name', 'link', 'icon']}
                        selection={selection}
                        onSelectionChange={(e) => setSelection(e.value)}
                        selectionMode="checkbox"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                        showGridlines
                        tableStyle={{ width: '100%', tableLayout: 'fixed' }}
                        className="p-datatable-sm sub-masters-table"
                        emptyMessage={statusFilter === 'All' ? 'No modules found' : `No ${statusFilter.toLowerCase()} user modules found`}
                    >
                        <Column selectionMode="multiple" style={{ width: '4%' }} />
                        <Column field="client_name" header="Client" sortable style={{ width: '11%' }} />
                        <Column field="branch_name" header="Branch" sortable style={{ width: '10%' }} />
                        <Column field="username" header="User" sortable style={{ width: '10%' }} />
                        <Column field="module_group_name" header="Module Group" sortable filter showFilterMenu={false} style={{ width: '12%' }} />
                        <Column field="module_name" header="Module Name" sortable style={{ width: '12%' }} />
                        <Column field="link" header="Link" sortable style={{ width: '12%' }} />
                        <Column field="icon" header="Icon" body={iconBodyTemplate} style={{ width: '15%' }} />
                        <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ width: '10%', textAlign: 'center' }} />
                    </DataTable>
                </div>
            )}
        </Card>
    );
};

export default UserModulesContent;
